import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Building2, Users, Phone, DollarSign, TrendingUp,
  Loader2, RefreshCw, BarChart3, Shield, MessageSquare,
  ArrowLeft, Globe, Layers, Gift, Coins, UserCheck, Plus, UserPlus,
  Trash2, StickyNote, ChevronDown, ChevronUp, Send, Copy, Wallet, LockOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { PlatformCosts } from '../components/PlatformCosts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TICKET_STATUS_META = {
  open: { label: 'Open', dot: 'bg-amber-400', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  in_progress: { label: 'In Progress', dot: 'bg-blue-400', chip: 'bg-blue-50 text-blue-700 border-blue-200' },
  resolved: { label: 'Resolved', dot: 'bg-emerald-400', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

function TicketCard({ ticket, onChanged, assignees = [], meEmail }) {
  const [busy, setBusy] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const status = ticket.status || 'open';
  const notes = ticket.notes || [];
  const replies = ticket.replies || [];
  const assignedEmail = ticket.assigned_to?.email || '';
  const assignedToMe = assignedEmail && assignedEmail === meEmail;

  const assign = async (email) => {
    if (assignBusy) return;
    setAssignBusy(true);
    try {
      const { data } = await axios.patch(`${API}/platform/tickets/${ticket.id}/assign`, { assignee_email: email || null });
      toast.success(data.message);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign ticket');
    } finally {
      setAssignBusy(false);
    }
  };

  const setStatus = async (next) => {
    if (next === status || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/platform/tickets/${ticket.id}/status`, { status: next });
      toast.success(next === 'resolved' && data.resolved_email_sent
        ? 'Marked resolved — customer notified by email'
        : data.message);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      const { data } = await axios.post(`${API}/platform/tickets/${ticket.id}/reply`, { message: replyText.trim() });
      if (data.email_sent) {
        toast.success(data.message);
      } else {
        toast.warning(data.message);
      }
      setReplyText('');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/platform/tickets/${ticket.id}/notes`, { note: noteText.trim() });
      setNoteText('');
      toast.success('Note added');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div className="border border-zinc-200 rounded-lg p-4 bg-white" data-testid={`ticket-card-${ticket.id}`}>
      <div className="flex items-center justify-between mb-2 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2 h-2 rounded-full shrink-0 ${TICKET_STATUS_META[status]?.dot || 'bg-zinc-300'}`} />
          <span className="font-medium text-zinc-900 text-sm truncate">{ticket.subject || '(no subject)'}</span>
          <Badge variant="outline" className="text-xs shrink-0">{ticket.category}</Badge>
          {ticket.assigned_to && (
            <span
              className={`shrink-0 flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${
                assignedToMe ? 'bg-brand-muted text-brand-primary border-brand-accent' : 'bg-zinc-100 text-zinc-600 border-zinc-200'
              }`}
              data-testid={`ticket-assignee-${ticket.id}`}
              title={`Assigned to ${ticket.assigned_to.email}`}
            >
              <UserCheck className="w-3 h-3" />
              {assignedToMe ? 'You' : (ticket.assigned_to.name || ticket.assigned_to.email)}
            </span>
          )}
        </div>
        <span className="text-xs text-zinc-400 shrink-0">{new Date(ticket.created_at).toLocaleString()}</span>
      </div>
      <p className="text-sm text-zinc-600 mb-2 whitespace-pre-wrap">{ticket.message}</p>
      <div className="text-xs text-zinc-400 mb-3">
        From: {ticket.user_name} ({ticket.user_email}) — {ticket.org_name || 'N/A'}
        {ticket.status_updated_by && (
          <span className="ml-2 text-zinc-300">•</span>
        )}
        {ticket.status_updated_by && (
          <span className="ml-2">Last updated by {ticket.status_updated_by}</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {Object.entries(TICKET_STATUS_META).map(([key, meta]) => (
          <button
            key={key}
            onClick={() => setStatus(key)}
            disabled={busy}
            className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
              status === key
                ? meta.chip
                : 'bg-white text-zinc-400 border-zinc-200 hover:text-zinc-600 hover:border-zinc-300'
            } ${busy ? 'opacity-50 cursor-wait' : ''}`}
            data-testid={`ticket-status-${key}-${ticket.id}`}
          >
            {meta.label}
          </button>
        ))}
        <span className="mx-1 w-px h-4 bg-zinc-200" />
        {!assignedEmail ? (
          <button
            onClick={() => assign(meEmail)}
            disabled={assignBusy}
            className={`px-2.5 py-1 text-xs font-medium rounded-full border border-brand-accent bg-brand-muted text-brand-primary hover:opacity-80 transition-opacity ${assignBusy ? 'opacity-50 cursor-wait' : ''}`}
            data-testid={`ticket-claim-${ticket.id}`}
          >
            Claim
          </button>
        ) : assignedToMe ? (
          <button
            onClick={() => assign(null)}
            disabled={assignBusy}
            className={`px-2.5 py-1 text-xs font-medium rounded-full border border-zinc-200 bg-white text-zinc-500 hover:text-zinc-700 hover:border-zinc-300 transition-colors ${assignBusy ? 'opacity-50 cursor-wait' : ''}`}
            data-testid={`ticket-release-${ticket.id}`}
          >
            Release
          </button>
        ) : null}
        {assignees.length > 1 && (
          <select
            value={assignedEmail}
            onChange={(e) => assign(e.target.value || null)}
            disabled={assignBusy}
            className="px-2 py-1 text-xs text-zinc-500 bg-white border border-zinc-200 rounded-full hover:border-zinc-300 focus:outline-none cursor-pointer max-w-[160px]"
            data-testid={`ticket-assign-select-${ticket.id}`}
            title="Assign to a team member"
          >
            <option value="">Unassigned</option>
            {assignees.map((a) => (
              <option key={a.email} value={a.email}>{a.name || a.email}</option>
            ))}
          </select>
        )}
        <button
          onClick={() => setReplyOpen(v => !v)}
          className="ml-auto flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-brand-primary hover:opacity-80 rounded-full border border-brand-accent bg-brand-muted transition-opacity"
          data-testid={`ticket-reply-toggle-${ticket.id}`}
        >
          <Send className="w-3 h-3" />
          Reply{replies.length > 0 ? ` (${replies.length})` : ''}
          {replyOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        <button
          onClick={() => setNotesOpen(v => !v)}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-zinc-500 hover:text-zinc-700 rounded-full border border-zinc-200 hover:bg-zinc-50 transition-colors"
          data-testid={`ticket-notes-toggle-${ticket.id}`}
        >
          <StickyNote className="w-3 h-3" />
          Notes{notes.length > 0 ? ` (${notes.length})` : ''}
          {notesOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {replyOpen && (
        <div className="mt-3 border-t border-zinc-100 pt-3 space-y-2" data-testid={`ticket-reply-panel-${ticket.id}`}>
          {replies.length > 0 && replies.map((r, i) => (
            <div key={i} className="bg-brand-muted/40 border border-brand-accent/40 rounded-md px-3 py-2">
              <p className="text-xs text-zinc-700 whitespace-pre-wrap">{r.text}</p>
              <p className="text-[10px] text-zinc-400 mt-1">
                {r.author_name || r.author_email} — {new Date(r.at).toLocaleString()}
                {r.email_sent === false && <span className="text-red-500 ml-1">(email failed)</span>}
              </p>
            </div>
          ))}
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={`Write a reply to ${ticket.user_name || ticket.user_email}… it will be emailed to ${ticket.user_email}`}
            className="rounded-md text-xs min-h-[70px]"
            data-testid={`ticket-reply-input-${ticket.id}`}
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={sendReply}
              disabled={sendingReply || !replyText.trim()}
              className="rounded-md h-8 text-xs bg-brand-primary hover:bg-brand-hover text-white"
              data-testid={`ticket-reply-send-${ticket.id}`}
            >
              {sendingReply ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
              {sendingReply ? 'Sending…' : 'Send reply'}
            </Button>
          </div>
        </div>
      )}

      {notesOpen && (
        <div className="mt-3 border-t border-zinc-100 pt-3 space-y-2" data-testid={`ticket-notes-panel-${ticket.id}`}>
          {notes.length > 0 ? notes.map((n, i) => (
            <div key={i} className="bg-zinc-50 border border-zinc-100 rounded-md px-3 py-2">
              <p className="text-xs text-zinc-700 whitespace-pre-wrap">{n.text}</p>
              <p className="text-[10px] text-zinc-400 mt-1">{n.author_name || n.author_email} — {new Date(n.at).toLocaleString()}</p>
            </div>
          )) : (
            <p className="text-xs text-zinc-400">No internal notes yet.</p>
          )}
          <div className="flex gap-2">
            <Input
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addNote()}
              placeholder="Add an internal note (staff-only)…"
              className="rounded-md h-8 text-xs"
              data-testid={`ticket-note-input-${ticket.id}`}
            />
            <Button
              size="sm"
              onClick={addNote}
              disabled={savingNote || !noteText.trim()}
              className="rounded-md h-8 text-xs bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid={`ticket-note-add-${ticket.id}`}
            >
              {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StaffPanel() {
  const [staff, setStaff] = useState([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/platform/staff`);
      setStaff(data);
    } catch (e) { /* owner-only; ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/platform/staff`, { email: email.trim() });
      toast.success(data.message);
      if (data.note) toast.warning(data.note, { duration: 8000 });
      setEmail('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add staff member');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (staffEmail) => {
    if (!window.confirm(`Remove ${staffEmail} from support staff? They will lose access to the tickets view.`)) return;
    try {
      const { data } = await axios.delete(`${API}/platform/staff/${encodeURIComponent(staffEmail)}`);
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove staff member');
    }
  };

  return (
    <div className="mb-5 border border-zinc-200 rounded-lg bg-zinc-50/60" data-testid="staff-panel">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="staff-panel-toggle"
      >
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-zinc-500" />
          <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Support Staff Access</span>
          <Badge variant="outline" className="text-xs">{staff.length}</Badge>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-xs text-zinc-500">
            Staff on this list can open the Support Tickets view (tickets only — no billing or org controls).
            They sign in with their normal PipeGuide account at <span className="font-mono">/platform</span>.
          </p>
          <div className="flex gap-2">
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && add()}
              placeholder="employee@yourcompany.com"
              className="rounded-md bg-white h-9"
              data-testid="staff-email-input"
            />
            <Button
              size="sm"
              onClick={add}
              disabled={busy || !email.trim()}
              className="rounded-md h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="staff-add-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>}
            </Button>
          </div>
          {staff.length > 0 && (
            <div className="space-y-1.5">
              {staff.map((s) => (
                <div key={s.email} className="flex items-center justify-between bg-white border border-zinc-200 rounded-md px-3 py-2">
                  <div>
                    <span className="text-sm text-zinc-800">{s.email}</span>
                    <span className="text-[10px] text-zinc-400 ml-2">added {new Date(s.added_at).toLocaleDateString()}</span>
                  </div>
                  <button
                    onClick={() => remove(s.email)}
                    className="text-zinc-400 hover:text-red-600 transition-colors"
                    data-testid={`staff-remove-${s.email}`}
                    title="Remove access"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LockedAccountsCard() {
  const [locked, setLocked] = useState(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/platform/locked-accounts`);
      setLocked(data.locked || []);
    } catch (e) {
      setLocked([]);
    }
  };
  useEffect(() => { load(); }, []);

  const unlock = async (target) => {
    const addr = (target || email).trim();
    if (!addr) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/platform/unlock-account`, { email: addr });
      toast.success(data.records_cleared > 0
        ? `Login unlocked for ${data.email} (${data.records_cleared} lockout record${data.records_cleared === 1 ? '' : 's'} cleared)`
        : `No lockout records found for ${data.email}`);
      setEmail('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unlock account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-zinc-200" data-testid="locked-accounts-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-2">
          <LockOpen className="w-4 h-4" />
          Login Lockouts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="user@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && unlock()}
            className="h-9 rounded-md flex-1 max-w-sm"
            data-testid="unlock-email-input"
          />
          <Button size="sm" onClick={() => unlock()} disabled={busy || !email.trim()} className="rounded-md bg-brand-primary hover:bg-brand-hover" data-testid="unlock-account-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Unlock login'}
          </Button>
        </div>
        {locked === null ? (
          <p className="text-xs text-zinc-400">Loading…</p>
        ) : locked.length === 0 ? (
          <p className="text-xs text-zinc-400" data-testid="no-locked-accounts">No locked-out logins right now.</p>
        ) : (
          <div className="space-y-1.5" data-testid="locked-accounts-list">
            {locked.map((l, i) => (
              <div key={i} className="flex items-center justify-between text-sm border border-zinc-100 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <span className="font-medium text-zinc-700 truncate">{l.email}</span>
                  <span className="text-xs text-zinc-400 ml-2">{l.failed_attempts} failed attempts{l.still_locked ? '' : ' · lock expired'}</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => unlock(l.email)} disabled={busy} className="rounded-md shrink-0 ml-2" data-testid={`unlock-row-btn-${i}`}>
                  Unlock
                </Button>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-zinc-400">5 failed password attempts lock a login for 15 minutes. Unlocking clears the counter immediately across all IPs.</p>
      </CardContent>
    </Card>
  );
}

function DemoOrgCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null); // 'reseed' | 'pro' | 'trial' | null

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/platform/demo/status`);
      setStatus(data);
    } catch (e) { /* non-fatal */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reseed = async () => {
    if (!window.confirm('Reseed the demo org? This wipes Demo Company completely and recreates it fresh: admin + 2 reps + 10 calls on a starter trial.')) return;
    setBusy('reseed');
    try {
      const { data } = await axios.post(`${API}/platform/demo/reseed`);
      toast.success(data.message);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reseed failed');
    } finally {
      setBusy(null);
    }
  };

  const switchPlan = async (mode) => {
    setBusy(mode);
    try {
      const { data } = await axios.post(`${API}/platform/demo/plan`, { mode });
      toast.success(data.message);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Plan switch failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="border-zinc-200" data-testid="demo-org-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-2">
          <Gift className="w-4 h-4" />
          Demo Org
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!status ? (
          <div className="flex items-center gap-2 text-sm text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="text-sm">
              {status.exists ? (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-zinc-900">{status.org_name}</span>
                    <PlanBadge plan={status.plan} />
                  </div>
                  <div className="text-xs text-zinc-500" data-testid="demo-org-status">
                    {status.calls} calls · {status.reps} reps
                    {status.tokens != null && <> · {status.tokens}/{status.token_allowance} tokens</>}
                  </div>
                  <div className="text-xs text-zinc-600 mt-1 font-mono bg-zinc-50 border border-zinc-200 rounded px-2 py-1 inline-flex items-center gap-2" data-testid="demo-org-credentials">
                    {status.admin_email} / {status.admin_password}
                    <button
                      type="button"
                      className="text-zinc-400 hover:text-zinc-700"
                      title="Copy credentials"
                      data-testid="demo-copy-credentials-btn"
                      onClick={() => { navigator.clipboard.writeText(`${status.admin_email} / ${status.admin_password}`); toast.success('Credentials copied'); }}
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </>
              ) : (
                <span className="text-zinc-500" data-testid="demo-org-status">No demo org yet ({status.admin_email}) — reseed to create it.</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {status.exists && status.plan !== 'pro' && (
                <Button size="sm" className="rounded-md bg-brand hover:bg-brand/90 text-white" style={{ backgroundColor: '#0E4E5A' }}
                  onClick={() => switchPlan('pro')} disabled={!!busy} data-testid="demo-switch-pro-btn">
                  {busy === 'pro' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-1.5" />}
                  Switch to Pro
                </Button>
              )}
              {status.exists && status.plan === 'pro' && (
                <Button size="sm" variant="outline" className="rounded-md"
                  onClick={() => switchPlan('trial')} disabled={!!busy} data-testid="demo-switch-trial-btn">
                  {busy === 'trial' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Layers className="w-4 h-4 mr-1.5" />}
                  Switch to Trial
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-md text-zinc-600"
                onClick={reseed} disabled={!!busy} data-testid="demo-reseed-btn">
                {busy === 'reseed' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                {status.exists ? 'Reseed Demo Org' : 'Create Demo Org'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({ label, value, sub, icon: Icon, color = 'text-zinc-700' }) {
  return (
    <Card className="border-zinc-200">
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-1">{label}</div>
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            {sub && <div className="text-xs text-zinc-400 mt-0.5">{sub}</div>}
          </div>
          {Icon && <Icon className="w-8 h-8 text-zinc-200" />}
        </div>
      </CardContent>
    </Card>
  );
}

function PlanBadge({ plan }) {
  const styles = {
    starter: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    pro: 'bg-brand-muted text-brand-primary border-brand-accent',
    business: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[plan] || styles.starter}`}>
      {plan?.charAt(0).toUpperCase() + plan?.slice(1) || 'Starter'}
    </span>
  );
}

export default function PlatformDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() =>
    new URLSearchParams(window.location.search).get('tab') === 'tickets' ? 'tickets' : 'overview'
  );
  const [grantTarget, setGrantTarget] = useState(null);
  const [topUpTarget, setTopUpTarget] = useState(null);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [membersTarget, setMembersTarget] = useState(null);
  const [lifecycleTarget, setLifecycleTarget] = useState(null);
  const [access, setAccess] = useState(null); // { is_owner, is_staff } | null while loading
  const [accessError, setAccessError] = useState(null);
  const [sectionErrors, setSectionErrors] = useState({});
  const [ticketFilter, setTicketFilter] = useState('all');
  const [assignees, setAssignees] = useState([]);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgPage, setOrgPage] = useState(1);
  const [orgTotal, setOrgTotal] = useState(0);
  const [orgTotalPages, setOrgTotalPages] = useState(1);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const orgSearchInitRef = useRef(false);

  const fetchOrgs = useCallback(async (page = 1, search = '') => {
    setOrgsLoading(true);
    try {
      const { data } = await axios.get(`${API}/platform/orgs`, { params: { page, search, page_size: 25 } });
      setOrgs(data.orgs);
      setOrgTotal(data.total);
      setOrgTotalPages(data.total_pages);
      setOrgPage(data.page);
    } catch (e) {
      toast.error('Failed to load organizations list');
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  // Debounced server-side search
  useEffect(() => {
    if (!orgSearchInitRef.current) { orgSearchInitRef.current = true; return; }
    const t = setTimeout(() => fetchOrgs(1, orgSearch), 400);
    return () => clearTimeout(t);
  }, [orgSearch, fetchOrgs]);
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [repairLoading, setRepairLoading] = useState(false);

  const runLookup = async () => {
    const email = lookupEmail.trim();
    if (!email) return;
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const { data } = await axios.get(`${API}/platform/lookup`, { params: { email } });
      setLookupResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  const runRepair = async () => {
    if (!lookupResult?.email) return;
    if (!window.confirm(`Create a new organization for ${lookupResult.email}? They will get a fresh org, sample call, and free trial.`)) return;
    setRepairLoading(true);
    try {
      const { data } = await axios.post(`${API}/platform/repair-org`, { email: lookupResult.email });
      toast.success(data.message);
      await runLookup();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairLoading(false);
    }
  };

  const impersonate = async (org) => {
    const reason = window.prompt(
      `Open ${org.name}'s account in a new tab?\n\n` +
        `This creates a 30-minute support session and is logged in the audit trail. ` +
        `Briefly describe the reason (e.g. "debugging bot join issue"):`,
      ''
    );
    if (reason === null) return;  // user cancelled
    try {
      const { data } = await axios.post(`${API}/platform/impersonate`, {
        org_id: org.id,
        reason: reason || 'Not provided',
      });
      // Stash the impersonation token in sessionStorage of a new tab so the
      // platform owner's own session in this tab isn't disturbed.
      const newTab = window.open('about:blank', '_blank');
      if (!newTab) {
        toast.error('Pop-up blocked. Allow pop-ups and try again.');
        return;
      }
      newTab.document.write(`
        <html><body style="font-family:system-ui; padding:40px; max-width:560px; margin:0 auto;">
          <h2 style="color:#0E4E5A;">Opening ${org.name}…</h2>
          <p>Setting up a 30-minute support session as <strong>${data.target_user.email}</strong>.</p>
          <p style="color:#71717A; font-size:14px;">Auto-redirecting in 1 second.</p>
          <script>
            localStorage.setItem('token', '${data.access_token}');
            localStorage.setItem('impersonation_banner', JSON.stringify({
              actor: 'platform owner',
              target: '${data.target_user.email}',
              org: '${data.target_user.org_name}',
              expires_at: ${Date.now() + 1800000}
            }));
            setTimeout(() => { window.location.href = '/dashboard'; }, 1000);
          </script>
        </body></html>
      `);
      newTab.document.close();
      toast.success(`Impersonating ${data.target_user.email} — opened in new tab`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impersonation failed');
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setAccessError(null);
    try {
      const { data: acc } = await axios.get(`${API}/platform/access`);
      setAccess(acc);
      if (acc.is_owner) {
        // Independent loads — one slow/failed endpoint must never blank the rest
        const [statsRes, orgsRes, ticketsRes, assigneesRes] = await Promise.allSettled([
          axios.get(`${API}/platform/stats`),
          axios.get(`${API}/platform/orgs`, { params: { page: 1, page_size: 25 } }),
          axios.get(`${API}/platform/tickets`),
          axios.get(`${API}/platform/assignees`),
        ]);
        const errDetail = (r) => r.reason?.response?.status
          ? `HTTP ${r.reason.response.status}${r.reason.response.data?.detail ? ` — ${r.reason.response.data.detail}` : ''}`
          : 'network error';
        const errs = {};
        if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
        else { errs.stats = errDetail(statsRes); toast.error(`Failed to load platform stats (${errs.stats})`, { duration: 10000 }); }
        if (orgsRes.status === 'fulfilled') {
          setOrgs(orgsRes.value.data.orgs);
          setOrgTotal(orgsRes.value.data.total);
          setOrgTotalPages(orgsRes.value.data.total_pages);
          setOrgPage(orgsRes.value.data.page);
        }
        else { errs.orgs = errDetail(orgsRes); toast.error(`Failed to load organizations (${errs.orgs})`, { duration: 10000 }); }
        if (ticketsRes.status === 'fulfilled') setTickets(ticketsRes.value.data);
        else { errs.tickets = errDetail(ticketsRes); toast.error(`Failed to load support tickets (${errs.tickets})`, { duration: 10000 }); }
        if (assigneesRes.status === 'fulfilled') setAssignees(assigneesRes.value.data);
        setSectionErrors(errs);
      } else if (acc.is_staff) {
        const [ticketsRes, assigneesRes] = await Promise.all([
          axios.get(`${API}/platform/tickets`),
          axios.get(`${API}/platform/assignees`),
        ]);
        setTickets(ticketsRes.data);
        setAssignees(assigneesRes.data);
        setActiveTab('tickets');
      }
    } catch (e) {
      console.error('Platform data error:', e);
      setAccessError(`${e.response?.status ? `HTTP ${e.response.status}` : 'Network error'}${e.response?.data?.detail ? ` — ${e.response.data.detail}` : ''}`);
      toast.error('Failed to check platform access');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshTickets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/platform/tickets`);
      setTickets(data);
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const planColors = { starter: '#71717A', pro: '#0E4E5A', business: '#7C3AED' };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="platform-dashboard">
      {/* Header */}
      <header className="h-16 border-b border-zinc-200 flex items-center justify-between px-6 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-zinc-900 rounded-md flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>PipeGuide</span>
          <Badge variant="outline" className="ml-1 text-xs text-zinc-600 border-zinc-300 bg-zinc-50">
            {!access || access.is_owner ? 'Platform Owner' : access.is_staff ? 'Support Staff' : 'Platform'}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Link to={access && !access.is_owner ? '/' : '/admin'}>
            <Button variant="ghost" size="sm" className="rounded-md text-zinc-500" data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> {access && !access.is_owner ? 'Back to App' : 'Back to Admin'}
            </Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={logout} className="rounded-md text-zinc-500" data-testid="platform-logout">
            Sign Out
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }} data-testid="platform-title">
              {!access || access.is_owner ? 'Platform Analytics' : access.is_staff ? 'Support Tickets' : 'Platform'}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              {!access || access.is_owner ? 'Cross-tenant overview of PipeGuide' : access.is_staff ? 'Manage customer support requests' : ''}
            </p>
          </div>
          {(!access || access.is_staff || access.is_owner) && (
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="rounded-md" data-testid="platform-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          )}
        </div>

        {/* Access check failed (network / server error) */}
        {accessError && !access ? (
          <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="platform-access-error">
            <Shield className="w-10 h-10 text-red-300 mb-4" />
            <h2 className="text-lg font-semibold text-zinc-900 mb-1">Couldn't verify platform access</h2>
            <p className="text-sm text-zinc-500 max-w-md">
              The access check request failed: <span className="font-mono text-red-600">{accessError}</span>
            </p>
            <p className="text-xs text-zinc-400 mt-2 max-w-md">
              Signed in as <span className="font-medium text-zinc-600">{user?.email}</span>. This is usually a temporary
              server or network issue — retry below. If it persists, check the backend logs for /api/platform/access.
            </p>
            <Button variant="outline" size="sm" onClick={fetchData} className="rounded-md mt-4" data-testid="access-retry-btn">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Retry
            </Button>
          </div>
        ) : access && !access.is_owner && !access.is_staff ? (
          <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="platform-no-access">
            <Shield className="w-10 h-10 text-zinc-200 mb-4" />
            <h2 className="text-lg font-semibold text-zinc-900 mb-1">No platform access</h2>
            <p className="text-sm text-zinc-500 max-w-sm">
              This area is limited to the platform owner and support staff. If you believe you should
              have access, ask the platform owner to add your email to the support staff list.
            </p>
            <p className="text-xs text-zinc-600 mt-3 max-w-md" data-testid="no-access-signed-in-as">
              You are signed in as <span className="font-medium text-zinc-600">{user?.email}</span>.
              If this IS the platform owner account, the PLATFORM_OWNER_EMAIL environment variable is
              missing or different in this deployment — contact support to fix the deployment config.
            </p>
            <Link to="/" className="mt-4">
              <Button variant="outline" size="sm" className="rounded-md">Back to App</Button>
            </Link>
          </div>
        ) : (
        <>
        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-zinc-200">
          {(access?.is_owner ? [
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'orgs', label: 'Organizations', icon: Building2 },
            { id: 'costs', label: 'Costs & Margins', icon: Wallet },
            { id: 'tickets', label: 'Support Tickets', icon: MessageSquare },
          ] : [
            { id: 'tickets', label: 'Support Tickets', icon: MessageSquare },
          ]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-zinc-900 text-zinc-900'
                  : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'tickets' && tickets.filter(t => t.status === 'open').length > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {tickets.filter(t => t.status === 'open').length}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24" data-testid="platform-loading">
            <Loader2 className="w-8 h-8 text-zinc-400 animate-spin mb-4" />
            <p className="text-zinc-500">Loading platform data...</p>
          </div>
        ) : activeTab === 'overview' ? (
          sectionErrors.stats && !stats ? (
            <div className="border border-red-200 bg-red-50 rounded-lg p-8 text-center" data-testid="stats-error">
              <p className="text-sm font-semibold text-red-700 mb-1">Failed to load platform stats</p>
              <p className="text-xs text-red-500 font-mono mb-4">{sectionErrors.stats}</p>
              <Button variant="outline" size="sm" onClick={fetchData} className="rounded-md" data-testid="stats-retry-btn">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Retry
              </Button>
            </div>
          ) : (
          <div className="space-y-6">
            {/* Top Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="platform-stats">
              <StatCard label="Organizations" value={stats?.total_orgs || 0} icon={Building2} />
              <StatCard label="Total Users" value={stats?.total_users || 0} icon={Users} sub={`${stats?.role_distribution?.admin || 0} admins, ${stats?.role_distribution?.manager || 0} mgrs, ${stats?.role_distribution?.sales || 0} reps`} />
              <StatCard label="Total Calls" value={stats?.total_sessions || 0} icon={Phone} sub={`${stats?.total_completed_sessions || 0} completed`} />
              <StatCard label="Est. MRR" value={`R${stats?.estimated_mrr?.toLocaleString() || 0}`} icon={DollarSign} color="text-emerald-600" />
              <StatCard label="Avg Score" value={stats?.platform_avg_score || 0} icon={TrendingUp} />
              <StatCard label="Support" value={`${stats?.support_tickets?.open || 0} open`} icon={MessageSquare} sub={`${stats?.support_tickets?.total || 0} total`} />
            </div>

            {/* Billing health — surfaces dunning + lifecycle status at a glance */}
            <BillingHealthCard health={stats?.billing_health} onViewOrgs={() => setActiveTab('orgs')} />

            {/* Demo org controls — reseed + plan switching for sales demos */}
            <DemoOrgCard />

            {/* Login lockout management — unlock any user instantly */}
            <LockedAccountsCard />

            {/* Plan Distribution + Framework Usage */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-zinc-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    Plan Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(stats?.plan_distribution || {}).map(([plan, count]) => {
                    const total = Object.values(stats?.plan_distribution || {}).reduce((a, b) => a + b, 0);
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                      <div key={plan} className="flex items-center gap-3">
                        <PlanBadge plan={plan} />
                        <div className="flex-1 bg-zinc-100 rounded-full h-2.5 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${pct}%`, backgroundColor: planColors[plan] || '#71717A' }}
                          />
                        </div>
                        <span className="text-sm font-medium text-zinc-700 w-16 text-right">{count} ({pct}%)</span>
                      </div>
                    );
                  })}
                  {!stats?.plan_distribution || Object.keys(stats.plan_distribution).length === 0 ? (
                    <p className="text-sm text-zinc-400">No subscription data yet</p>
                  ) : null}
                </CardContent>
              </Card>

              <Card className="border-zinc-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Framework Usage
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(stats?.framework_usage || {}).sort((a, b) => b[1] - a[1]).map(([fw, count]) => {
                    const total = Object.values(stats?.framework_usage || {}).reduce((a, b) => a + b, 0);
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                      <div key={fw} className="flex items-center gap-3">
                        <span className="text-sm text-zinc-600 w-28 truncate font-medium">{fw.toUpperCase()}</span>
                        <div className="flex-1 bg-zinc-100 rounded-full h-2.5 overflow-hidden">
                          <div className="h-full rounded-full bg-brand-muted0 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-sm text-zinc-700 w-16 text-right">{count}</span>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Recent Signups */}
            <Card className="border-zinc-200" data-testid="recent-signups">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
                  Recent Signups (Last 30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.recent_orgs?.length ? (
                  <div className="space-y-2">
                    {stats.recent_orgs.map((org, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                        <div>
                          <span className="font-medium text-zinc-900 text-sm">{org.name}</span>
                          <span className="text-xs text-zinc-400 ml-2">{org.owner_email}</span>
                        </div>
                        <span className="text-xs text-zinc-400">{new Date(org.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-400">No new signups in the last 30 days</p>
                )}
              </CardContent>
            </Card>
          </div>
          )
        ) : activeTab === 'orgs' ? (
          <Card className="border-zinc-200" data-testid="orgs-table">
            <CardContent className="pt-6">
              {sectionErrors.orgs && (
                <div className="mb-4 border border-red-200 bg-red-50 rounded-lg p-4 flex items-center justify-between" data-testid="orgs-error">
                  <div>
                    <p className="text-sm font-medium text-red-700">Failed to load organizations</p>
                    <p className="text-xs text-red-500 font-mono">{sectionErrors.orgs}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchData} className="rounded-md" data-testid="orgs-retry-btn">
                    <RefreshCw className="w-4 h-4 mr-1.5" /> Retry
                  </Button>
                </div>
              )}
              {/* Account lookup — diagnose "signed up but org not showing" */}
              <div className="mb-5 border border-zinc-200 rounded-lg p-4 bg-zinc-50/60" data-testid="account-lookup-panel">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">Account Lookup</div>
                <div className="flex gap-2">
                  <Input
                    value={lookupEmail}
                    onChange={(e) => setLookupEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && runLookup()}
                    placeholder="Search any account by email, e.g. name@gmail.com"
                    className="rounded-md bg-white"
                    data-testid="lookup-email-input"
                  />
                  <Button
                    size="sm"
                    onClick={runLookup}
                    disabled={lookupLoading || !lookupEmail.trim()}
                    className="rounded-md h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
                    data-testid="lookup-search-btn"
                  >
                    {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                  </Button>
                </div>
                {lookupResult && (
                  <div className="mt-3 text-sm" data-testid="lookup-result">
                    <div className={`rounded-md border p-3 ${
                      lookupResult.diagnosis === 'healthy' ? 'bg-emerald-50 border-emerald-200' :
                      lookupResult.diagnosis === 'no_user' ? 'bg-zinc-100 border-zinc-200' :
                      'bg-amber-50 border-amber-200'
                    }`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <span className="font-semibold text-zinc-900">{lookupResult.email}</span>
                          <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full border ${
                            lookupResult.diagnosis === 'healthy' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                            lookupResult.diagnosis === 'no_user' ? 'bg-zinc-200 text-zinc-600 border-zinc-300' :
                            'bg-amber-100 text-amber-700 border-amber-200'
                          }`} data-testid="lookup-diagnosis">
                            {lookupResult.diagnosis === 'healthy' ? 'Healthy' :
                             lookupResult.diagnosis === 'no_user' ? 'No account' :
                             lookupResult.diagnosis === 'missing_org' ? 'Missing organization' : 'Broken org link'}
                          </span>
                        </div>
                        {(lookupResult.diagnosis === 'missing_org' || lookupResult.diagnosis === 'dangling_org') && (
                          <Button
                            size="sm"
                            onClick={runRepair}
                            disabled={repairLoading}
                            className="rounded-md h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="repair-org-btn"
                          >
                            {repairLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Repair — create org'}
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-zinc-600 mt-1.5">{lookupResult.diagnosis_detail}</p>
                      {lookupResult.user && (
                        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-zinc-600">
                          <div><span className="text-zinc-400">Name:</span> {lookupResult.user.name || '—'}</div>
                          <div><span className="text-zinc-400">Role:</span> {lookupResult.user.role}</div>
                          <div><span className="text-zinc-400">Google:</span> {lookupResult.user.google_linked ? 'Yes' : 'No'}</div>
                          <div><span className="text-zinc-400">Verified:</span> {lookupResult.user.email_verified ? 'Yes' : 'No'}</div>
                          <div><span className="text-zinc-400">Signed up:</span> {lookupResult.user.created_at ? new Date(lookupResult.user.created_at).toLocaleString() : '—'}</div>
                          {lookupResult.org && (
                            <>
                              <div><span className="text-zinc-400">Org:</span> {lookupResult.org.name}</div>
                              <div><span className="text-zinc-400">Plan:</span> {lookupResult.org.plan}</div>
                              <div><span className="text-zinc-400">Calls:</span> {lookupResult.org.session_count}</div>
                            </>
                          )}
                        </div>
                      )}
                      {lookupResult.invitations?.length > 0 && (
                        <div className="mt-2.5 space-y-1.5" data-testid="lookup-invitations">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Invitations</p>
                          {lookupResult.invitations.map((inv) => (
                            <div key={inv.token} className="flex items-center justify-between gap-2 bg-white border border-zinc-200 rounded-md px-3 py-1.5">
                              <span className="text-xs text-zinc-600 truncate">
                                {inv.role} — <span className={inv.status === 'pending' ? 'text-amber-600 font-medium' : 'text-zinc-400'}>{inv.status}</span>
                                {inv.expires_at && <span className="text-zinc-400"> · expires {new Date(inv.expires_at).toLocaleDateString()}</span>}
                              </span>
                              {inv.status === 'pending' && (
                                <div className="shrink-0 flex items-center gap-1.5">
                                  <button
                                    onClick={async () => {
                                      try {
                                        const { data } = await axios.post(`${API}/platform/invitations/${inv.token}/resend`);
                                        if (data.email_sent) toast.success(data.message);
                                        else toast.warning(data.message);
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Failed to resend invitation');
                                      }
                                    }}
                                    className="px-2 py-0.5 text-[11px] font-medium rounded-full border border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 transition-colors"
                                    data-testid={`resend-invite-${inv.token}`}
                                  >
                                    Resend email
                                  </button>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(`${window.location.origin}/register?invite=${inv.token}`);
                                      toast.success('Invite link copied — share it with the user directly');
                                    }}
                                    className="px-2 py-0.5 text-[11px] font-medium rounded-full border border-brand-accent bg-brand-muted text-brand-primary hover:opacity-80 transition-opacity"
                                    data-testid={`copy-invite-link-${inv.token}`}
                                  >
                                    Copy invite link
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <Input
                    value={orgSearch}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    placeholder="Search organizations by name…"
                    className="rounded-md h-9 w-64"
                    data-testid="org-search-input"
                  />
                  <div className="text-xs text-zinc-500" data-testid="org-total-count">
                    {orgsLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <><span className="font-semibold text-zinc-900">{orgTotal}</span> organization{orgTotal === 1 ? '' : 's'}{orgSearch ? ' found' : ''}</>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => setCreateOrgOpen(true)}
                  className="rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white"
                  data-testid="create-org-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Create Organization
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left py-3 px-4 font-semibold text-zinc-500 text-xs uppercase tracking-wide">Organization</th>
                      <th className="text-center py-3 px-4 font-semibold text-zinc-500 text-xs uppercase tracking-wide">Plan</th>
                      <th className="text-center py-3 px-4 font-semibold text-zinc-500 text-xs uppercase tracking-wide">Members</th>
                      <th className="text-center py-3 px-4 font-semibold text-zinc-500 text-xs uppercase tracking-wide">Calls</th>
                      <th className="text-center py-3 px-4 font-semibold text-zinc-500 text-xs uppercase tracking-wide">Status</th>
                      <th className="text-right py-3 px-4 font-semibold text-zinc-500 text-xs uppercase tracking-wide">Created</th>
                      <th className="text-right py-3 px-4 font-semibold text-zinc-500 text-xs uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgs.map((org) => (
                      <tr key={org.id} className="border-b border-zinc-100 hover:bg-zinc-50/50 transition-colors">
                        <td className="py-3 px-4 font-medium text-zinc-900">{org.name}</td>
                        <td className="py-3 px-4 text-center"><PlanBadge plan={org.plan} /></td>
                        <td className="py-3 px-4 text-center text-zinc-600">{org.member_count}</td>
                        <td className="py-3 px-4 text-center text-zinc-600">{org.session_count}</td>
                        <td className="py-3 px-4 text-center">
                          <OrgStatusBadge status={org.org_status || 'active'} />
                        </td>
                        <td className="py-3 px-4 text-right text-zinc-400 text-xs">{new Date(org.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-1.5">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => impersonate(org)}
                              className="rounded-md text-xs h-7 px-2 text-zinc-700 border-zinc-300 hover:bg-zinc-100"
                              data-testid={`impersonate-btn-${org.id}`}
                              title="Open this org's dashboard in a new tab (30-min support session)"
                            >
                              <UserCheck className="w-3 h-3 mr-1" />
                              View as
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setMembersTarget(org)}
                              className="rounded-md text-xs h-7 px-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                              data-testid={`members-btn-${org.id}`}
                              title="View members and add new members to this org"
                            >
                              <UserPlus className="w-3 h-3 mr-1" />
                              Members
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setTopUpTarget(org)}
                              className="rounded-md text-xs h-7 px-2 text-amber-700 border-amber-200 hover:bg-amber-50"
                              data-testid={`top-up-tokens-btn-${org.id}`}
                            >
                              <Coins className="w-3 h-3 mr-1" />
                              Top Up
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setGrantTarget(org)}
                              className="rounded-md text-xs h-7 px-2 text-violet-700 border-violet-200 hover:bg-violet-50"
                              data-testid={`grant-plan-btn-${org.id}`}
                            >
                              <Gift className="w-3 h-3 mr-1" />
                              Grant Plan
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setLifecycleTarget(org)}
                              className="rounded-md text-xs h-7 px-2 text-red-700 border-red-200 hover:bg-red-50"
                              data-testid={`lifecycle-btn-${org.id}`}
                              title="Suspend / reactivate / terminate / delete this organization"
                            >
                              <Shield className="w-3 h-3 mr-1" />
                              Lifecycle
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {orgs.length === 0 && <p className="text-center text-zinc-400 py-8">{orgSearch ? 'No organizations match your search' : 'No organizations yet'}</p>}
              </div>

              {/* Pagination */}
              {orgTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4" data-testid="org-pagination">
                  <span className="text-xs text-zinc-500">
                    Page <span className="font-semibold text-zinc-900">{orgPage}</span> of {orgTotalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => fetchOrgs(orgPage - 1, orgSearch)}
                      disabled={orgPage <= 1 || orgsLoading}
                      className="rounded-md h-8 text-xs"
                      data-testid="org-page-prev"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => fetchOrgs(orgPage + 1, orgSearch)}
                      disabled={orgPage >= orgTotalPages || orgsLoading}
                      className="rounded-md h-8 text-xs"
                      data-testid="org-page-next"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : activeTab === 'costs' ? (
          <PlatformCosts />
        ) : activeTab === 'tickets' ? (
          <Card className="border-zinc-200" data-testid="tickets-table">
            <CardContent className="pt-6">
              {sectionErrors.tickets && (
                <div className="mb-4 border border-red-200 bg-red-50 rounded-lg p-4 flex items-center justify-between" data-testid="tickets-error">
                  <div>
                    <p className="text-sm font-medium text-red-700">Failed to load support tickets</p>
                    <p className="text-xs text-red-500 font-mono">{sectionErrors.tickets}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchData} className="rounded-md" data-testid="tickets-retry-btn">
                    <RefreshCw className="w-4 h-4 mr-1.5" /> Retry
                  </Button>
                </div>
              )}
              {access?.is_owner && <StaffPanel />}

              {/* Status filter */}
              <div className="flex items-center gap-1.5 mb-4 flex-wrap">
                {[['all', 'All'], ['mine', 'Mine'], ['open', 'Open'], ['in_progress', 'In Progress'], ['resolved', 'Resolved']].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setTicketFilter(key)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                      ticketFilter === key
                        ? 'bg-zinc-900 text-white border-zinc-900'
                        : 'bg-white text-zinc-500 border-zinc-200 hover:text-zinc-700 hover:border-zinc-300'
                    }`}
                    data-testid={`ticket-filter-${key}`}
                  >
                    {label}
                    {key === 'mine' && (
                      <span className="ml-1 opacity-60">
                        {tickets.filter(t => t.assigned_to?.email === user?.email).length}
                      </span>
                    )}
                    {key !== 'all' && key !== 'mine' && (
                      <span className="ml-1 opacity-60">
                        {tickets.filter(t => (t.status || 'open') === key).length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {(() => {
                const visible = tickets.filter(t => {
                  if (ticketFilter === 'all') return true;
                  if (ticketFilter === 'mine') return t.assigned_to?.email === user?.email;
                  return (t.status || 'open') === ticketFilter;
                });
                return visible.length > 0 ? (
                  <div className="space-y-3">
                    {visible.map((t) => (
                      <TicketCard key={t.id} ticket={t} onChanged={refreshTickets} assignees={assignees} meEmail={user?.email} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <MessageSquare className="w-10 h-10 text-zinc-200 mx-auto mb-3" />
                    <p className="text-sm text-zinc-400">
                      {tickets.length === 0 ? 'No support tickets yet' : 'No tickets match this filter'}
                    </p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        ) : null}
        </>
        )}
      </div>

      <GrantPlanDialog
        org={grantTarget}
        onClose={() => setGrantTarget(null)}
        onGranted={() => { setGrantTarget(null); fetchData(); }}
      />

      <TopUpTokensDialog
        org={topUpTarget}
        onClose={() => setTopUpTarget(null)}
        onGranted={() => { setTopUpTarget(null); fetchData(); }}
      />

      <CreateOrgDialog
        open={createOrgOpen}
        onClose={() => setCreateOrgOpen(false)}
        onCreated={() => { setCreateOrgOpen(false); fetchData(); }}
      />

      <OrgMembersDialog
        org={membersTarget}
        onClose={() => setMembersTarget(null)}
        onAdded={() => fetchData()}
      />

      <LifecycleDialog
        org={lifecycleTarget}
        onClose={() => setLifecycleTarget(null)}
        onChanged={() => { setLifecycleTarget(null); fetchData(); }}
      />
    </div>
  );
}

function GrantPlanDialog({ org, onClose, onGranted }) {
  const [plan, setPlan] = useState('pro');
  const [months, setMonths] = useState(1);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [note, setNote] = useState('');
  const [resetTokens, setResetTokens] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Reset form whenever a new org is selected
  useEffect(() => {
    if (org) {
      setPlan(org.plan && org.plan !== 'starter' ? org.plan : 'pro');
      setMonths(1);
      setBillingCycle('monthly');
      setNote('');
      setResetTokens(true);
    }
  }, [org]);

  const submit = async () => {
    if (!org) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/platform/grant-plan`, {
        org_id: org.id,
        plan,
        months: Number(months),
        billing_cycle: billingCycle,
        note: note.trim() || null,
        reset_tokens: resetTokens,
      });
      toast.success(data.message || 'Plan granted');
      onGranted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grant plan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!org} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="grant-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-violet-600" />
            Grant Plan Manually
          </DialogTitle>
          <DialogDescription>
            Use this when a customer has paid by EFT, wire transfer, or invoice outside Paystack.
            Their subscription will activate immediately and tokens will be granted.
          </DialogDescription>
        </DialogHeader>

        {org && (
          <div className="space-y-4 py-2">
            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-1">Organization</div>
              <div className="font-semibold text-zinc-900">{org.name}</div>
              <div className="text-xs text-zinc-500 mt-1">Currently on <span className="font-medium">{org.plan}</span></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700">Plan</label>
                <Select value={plan} onValueChange={setPlan}>
                  <SelectTrigger className="rounded-md" data-testid="grant-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter (free)</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700">Duration (months)</label>
                <Input
                  type="number" min={1} max={24}
                  value={months}
                  onChange={e => setMonths(e.target.value)}
                  className="rounded-md"
                  data-testid="grant-plan-months"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">Billing cycle (display only)</label>
              <Select value={billingCycle} onValueChange={setBillingCycle}>
                <SelectTrigger className="rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">Internal note (audit log)</label>
              <Textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="e.g. EFT received 2026-02-14, ref ABC-123. Paid R1,299 for Pro × 1 mo."
                className="rounded-md resize-none h-20"
                data-testid="grant-plan-note"
              />
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={resetTokens}
                onCheckedChange={(v) => setResetTokens(!!v)}
                data-testid="grant-plan-reset-tokens"
              />
              <span className="text-xs text-zinc-700">
                Refresh monthly token allowance to the new plan&apos;s level (recommended)
              </span>
            </label>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-md" data-testid="grant-plan-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-violet-600 hover:bg-violet-700 rounded-md"
            data-testid="grant-plan-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
            Grant {plan}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TopUpTokensDialog({ org, onClose, onGranted }) {
  const [amount, setAmount] = useState(25);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (org) {
      setAmount(25);
      setNote('');
    }
  }, [org]);

  const submit = async () => {
    if (!org) return;
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0 || amt > 10000) {
      toast.error('Enter a token amount between 1 and 10,000');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/platform/grant-tokens`, {
        org_id: org.id,
        amount: amt,
        note: note.trim() || null,
      });
      toast.success(data.message || `Credited ${amt} tokens`);
      onGranted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to credit tokens');
    } finally {
      setSubmitting(false);
    }
  };

  // Suggested preset amounts mirror the public top-up packs
  const presets = [10, 25, 50, 100];

  return (
    <Dialog open={!!org} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="top-up-tokens-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-amber-600" />
            Top Up Meeting Tokens
          </DialogTitle>
          <DialogDescription>
            Credit tokens directly to this org&apos;s overage pool. Use for customers who paid by EFT/wire,
            demo refills, or compensation. Action is recorded in the audit log.
          </DialogDescription>
        </DialogHeader>

        {org && (
          <div className="space-y-4 py-2">
            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-1">Organization</div>
              <div className="font-semibold text-zinc-900">{org.name}</div>
              <div className="text-xs text-zinc-500 mt-1">Plan: <span className="font-medium">{org.plan}</span></div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-zinc-700">Tokens to credit</label>
              <Input
                type="number" min={1} max={10000}
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="rounded-md"
                data-testid="top-up-tokens-amount"
              />
              <div className="flex gap-1.5">
                {presets.map(p => (
                  <Button
                    key={p}
                    type="button"
                    size="sm"
                    variant={Number(amount) === p ? 'default' : 'outline'}
                    className="rounded-md text-xs h-7 px-2.5"
                    onClick={() => setAmount(p)}
                    data-testid={`top-up-tokens-preset-${p}`}
                  >
                    {p}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-zinc-500">
                1 token = 30 minutes of bot recording time. R599 = 25 tokens, R999 = 50, R1,399 = 100.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">Internal note (audit log)</label>
              <Textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="e.g. EFT received 2026-02-14, ref ABC-123. 25-token pack."
                className="rounded-md resize-none h-20"
                data-testid="top-up-tokens-note"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-md" data-testid="top-up-tokens-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-amber-600 hover:bg-amber-700 rounded-md"
            data-testid="top-up-tokens-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Coins className="w-4 h-4 mr-2" />}
            Credit {amount} tokens
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =========================================================================
// CreateOrgDialog — provision a fresh org + primary admin from scratch
// =========================================================================
function CreateOrgDialog({ open, onClose, onCreated }) {
  const [orgName, setOrgName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [plan, setPlan] = useState('pro');
  const [months, setMonths] = useState(1);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [startingTokens, setStartingTokens] = useState(0);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setOrgName(''); setAdminEmail(''); setAdminName('');
      setPlan('pro'); setMonths(1); setBillingCycle('monthly');
      setStartingTokens(0); setNote('');
    }
  }, [open]);

  const valid = orgName.trim().length >= 2 && adminEmail.includes('@') && adminName.trim().length >= 1;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/platform/create-org`, {
        org_name: orgName.trim(),
        admin_email: adminEmail.trim().toLowerCase(),
        admin_name: adminName.trim(),
        plan,
        months: Number(months),
        billing_cycle: billingCycle,
        starting_tokens: Number(startingTokens) || 0,
        note: note.trim() || null,
      });
      if (data.invite_email_sent) {
        toast.success(data.message || 'Organization created');
        if (data.invite_link) {
          navigator.clipboard?.writeText(data.invite_link).catch(() => {});
          toast.info('Invite link also copied to clipboard', { duration: 5000 });
        }
      } else {
        toast.warning(data.message || 'Organization created, but the invite email failed to send.', { duration: 10000 });
        if (data.invite_link) {
          navigator.clipboard?.writeText(data.invite_link).catch(() => {});
          toast.info('Invite link copied to clipboard — share it manually', { duration: 10000 });
        }
      }
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="create-org-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-brand-primary" />
            Create New Organization
          </DialogTitle>
          <DialogDescription>
            Provision a new customer org and email the primary admin a set-password link.
            Use this for direct-billed customers (EFT, wire, month-to-month, annual) so they can
            be fully onboarded without registering themselves.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-700">Organization name *</label>
            <Input
              value={orgName}
              onChange={e => setOrgName(e.target.value)}
              placeholder="Acme Corp"
              className="rounded-md"
              data-testid="create-org-name"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">Admin name *</label>
              <Input
                value={adminName}
                onChange={e => setAdminName(e.target.value)}
                placeholder="Jane Doe"
                className="rounded-md"
                data-testid="create-org-admin-name"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">Admin email *</label>
              <Input
                type="email"
                value={adminEmail}
                onChange={e => setAdminEmail(e.target.value)}
                placeholder="jane@acme.com"
                className="rounded-md"
                data-testid="create-org-admin-email"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">Plan</label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger className="rounded-md" data-testid="create-org-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter (free)</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">Months</label>
              <Input
                type="number" min={1} max={24}
                value={months}
                onChange={e => setMonths(e.target.value)}
                disabled={plan === 'starter'}
                className="rounded-md"
                data-testid="create-org-months"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">Billing cycle</label>
              <Select value={billingCycle} onValueChange={setBillingCycle} disabled={plan === 'starter'}>
                <SelectTrigger className="rounded-md" data-testid="create-org-cycle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-700">Starting bonus tokens (optional)</label>
            <Input
              type="number" min={0} max={10000}
              value={startingTokens}
              onChange={e => setStartingTokens(e.target.value)}
              placeholder="0"
              className="rounded-md"
              data-testid="create-org-tokens"
            />
            <p className="text-xs text-zinc-500">
              Bonus tokens on top of the plan&apos;s monthly allowance. Useful for POC trials.
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-700">Internal note (audit log)</label>
            <Textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g. POC kickoff Feb 2026, 30-day evaluation"
              className="rounded-md resize-none h-20"
              data-testid="create-org-note"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-md" data-testid="create-org-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || !valid}
            className="bg-brand-primary hover:bg-brand-primary/90 text-white rounded-md"
            data-testid="create-org-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Create & Send Invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// OrgMembersDialog — view members of an org and add new ones
// =========================================================================
function OrgMembersDialog({ org, onClose, onAdded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('sales');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!org) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/platform/orgs/${org.id}/members`);
      setData(data);
    } catch (e) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [org]);

  useEffect(() => {
    if (org) {
      load();
      setShowAdd(false);
      setShowBulk(false);
      setNewEmail(''); setNewName(''); setNewRole('sales');
    }
  }, [org, load]);

  const submit = async () => {
    if (!org) return;
    setSubmitting(true);
    try {
      const { data: res } = await axios.post(`${API}/platform/orgs/${org.id}/add-member`, {
        email: newEmail.trim().toLowerCase(),
        name: newName.trim(),
        role: newRole,
      });
      toast.success(res.message);
      if (res.invite_link) {
        navigator.clipboard?.writeText(res.invite_link).catch(() => {});
        toast.info('Invite link copied to clipboard', { duration: 5000 });
      }
      if (!res.invite_email_sent) {
        toast.warning('Invite email failed to send. Use the copied link to share manually.', { duration: 10000 });
      }
      setShowAdd(false);
      setNewEmail(''); setNewName(''); setNewRole('sales');
      await load();
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add member');
    } finally {
      setSubmitting(false);
    }
  };

  const roleColor = (r) => ({
    admin: 'text-violet-700 bg-violet-50 border-violet-200',
    manager: 'text-amber-700 bg-amber-50 border-amber-200',
    sales: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  }[r] || 'text-zinc-700 bg-zinc-50 border-zinc-200');

  return (
    <Dialog open={!!org} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="org-members-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            {org?.name} — Team Members
          </DialogTitle>
          <DialogDescription>
            View all members of this organization and add new ones. New members get an email
            invitation to set their own password.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8" data-testid="members-loading">
            <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <div className="text-xs text-zinc-500">
                <span className="font-semibold text-zinc-900">{data?.members?.length || 0}</span> members
                {data?.pending_invitations?.length > 0 && (
                  <span className="ml-2">
                    · <span className="font-semibold text-amber-700">{data.pending_invitations.length}</span> pending invitations
                  </span>
                )}
              </div>
              {!showAdd && !showBulk && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowBulk(true)}
                    className="rounded-md text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    data-testid="bulk-invite-btn"
                  >
                    <Layers className="w-3.5 h-3.5 mr-1.5" />
                    Bulk CSV
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setShowAdd(true)}
                    className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="add-member-btn"
                  >
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                    Add Member
                  </Button>
                </div>
              )}
            </div>

            {showBulk && (
              <BulkInviteForm
                org={org}
                onCancel={() => setShowBulk(false)}
                onComplete={async () => {
                  setShowBulk(false);
                  await load();
                  onAdded();
                }}
              />
            )}

            {showAdd && (
              <div className="border border-emerald-200 bg-emerald-50/30 rounded-md p-4 space-y-3" data-testid="add-member-form">
                <div className="text-sm font-semibold text-zinc-900">Add a new member</div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Full name"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    className="rounded-md bg-white"
                    data-testid="add-member-name"
                  />
                  <Input
                    type="email"
                    placeholder="email@company.com"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    className="rounded-md bg-white"
                    data-testid="add-member-email"
                  />
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1 space-y-1.5">
                    <label className="text-xs font-medium text-zinc-700">Role</label>
                    <Select value={newRole} onValueChange={setNewRole}>
                      <SelectTrigger className="rounded-md bg-white" data-testid="add-member-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sales">Sales Rep</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setShowAdd(false); setNewEmail(''); setNewName(''); }}
                    className="rounded-md"
                    data-testid="add-member-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={submit}
                    disabled={submitting || !newEmail.includes('@') || newName.trim().length < 1}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md"
                    data-testid="add-member-confirm"
                  >
                    {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <UserPlus className="w-3.5 h-3.5 mr-1.5" />}
                    Add & Invite
                  </Button>
                </div>
              </div>
            )}

            <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100" data-testid="members-list">
              {data?.members?.length ? data.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-zinc-900 truncate">{m.name || '(no name)'}</div>
                    <div className="text-xs text-zinc-500 truncate">{m.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.must_reset_password && (
                      <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                        pending setup
                      </span>
                    )}
                    <span className={`text-xs font-medium border rounded-full px-2 py-0.5 ${roleColor(m.role)}`}>
                      {m.role}
                    </span>
                  </div>
                </div>
              )) : (
                <div className="text-center py-6 text-sm text-zinc-400">No members yet</div>
              )}
            </div>

            {data?.pending_invitations?.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Pending invitations ({data.pending_invitations.length})
                </div>
                <div className="border border-amber-200 bg-amber-50/40 rounded-md divide-y divide-amber-100">
                  {data.pending_invitations.map((inv) => (
                    <div key={inv.token} className="flex items-center justify-between px-4 py-2 text-sm">
                      <div>
                        <div className="font-medium text-zinc-900">{inv.email}</div>
                        <div className="text-xs text-zinc-500">
                          {inv.role} · invited {new Date(inv.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const origin = window.location.origin;
                          navigator.clipboard?.writeText(`${origin}/register?invite=${inv.token}`);
                          toast.success('Invite link copied');
                        }}
                        className="text-xs h-7 rounded-md"
                        data-testid={`copy-invite-${inv.token}`}
                      >
                        Copy invite link
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-md" data-testid="org-members-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// BulkInviteForm — paste a CSV of emails/names/roles and provision them all
// =========================================================================
function parseCsv(text) {
  // Lightweight CSV parser: handles `email,name,role` rows with or without a header.
  // Trims whitespace; skips empty lines and lines beginning with `#`.
  const out = [];
  const errors = [];
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l && !l.startsWith('#'));

  const headerCandidate = (lines[0] || '').toLowerCase();
  let startIdx = 0;
  if (headerCandidate.includes('email') && (headerCandidate.includes('name') || headerCandidate.includes('role'))) {
    startIdx = 1;
  }

  for (let i = startIdx; i < lines.length; i++) {
    const raw = lines[i];
    // Split on comma; allow tab or semicolon as fallback separators
    const cols = raw.includes(',') ? raw.split(',') : raw.includes(';') ? raw.split(';') : raw.split('\t');
    const [emailRaw = '', nameRaw = '', roleRaw = ''] = cols.map((c) => c.trim());
    const email = emailRaw.toLowerCase();
    const name = nameRaw;
    let role = (roleRaw || 'sales').toLowerCase();
    // Be friendly with role aliases
    if (['rep', 'sales rep', 'sales_rep', 'salesperson'].includes(role)) role = 'sales';
    if (['mgr'].includes(role)) role = 'manager';
    if (!['admin', 'manager', 'sales'].includes(role)) role = 'sales';

    if (!email.includes('@') || !email.includes('.')) {
      errors.push({ row: i + 1, raw, reason: 'invalid email' });
      continue;
    }
    out.push({ row: i + 1, email, name, role });
  }
  return { entries: out, errors };
}

function BulkInviteForm({ org, onCancel, onComplete }) {
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState({ entries: [], errors: [] });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setParsed(parseCsv(csvText));
  }, [csvText]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
  };

  const submit = async () => {
    if (parsed.entries.length === 0) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/platform/orgs/${org.id}/bulk-invite`, {
        entries: parsed.entries.map(({ email, name, role }) => ({ email, name, role })),
      });
      setResult(data);
      const okN = data.totals.successful;
      const skipN = data.totals.skipped;
      const failN = data.totals.failed;
      if (okN > 0) toast.success(`${okN} member${okN === 1 ? '' : 's'} added and invited`);
      if (skipN > 0) toast.info(`${skipN} skipped (already existed or duplicate in batch)`);
      if (failN > 0) toast.error(`${failN} failed validation — see results below`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk invite failed');
    } finally {
      setSubmitting(false);
    }
  };

  const finish = async () => {
    setResult(null);
    setCsvText('');
    await onComplete();
  };

  if (result) {
    return (
      <div className="border border-emerald-200 bg-emerald-50/30 rounded-md p-4 space-y-3" data-testid="bulk-invite-results">
        <div className="text-sm font-semibold text-zinc-900">Bulk invite results</div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-emerald-100 rounded-md py-3">
            <div className="text-2xl font-bold text-emerald-700" data-testid="bulk-result-success">{result.totals.successful}</div>
            <div className="text-xs text-emerald-700 uppercase tracking-wide">Added</div>
          </div>
          <div className="bg-amber-100 rounded-md py-3">
            <div className="text-2xl font-bold text-amber-700" data-testid="bulk-result-skipped">{result.totals.skipped}</div>
            <div className="text-xs text-amber-700 uppercase tracking-wide">Skipped</div>
          </div>
          <div className="bg-red-100 rounded-md py-3">
            <div className="text-2xl font-bold text-red-700" data-testid="bulk-result-failed">{result.totals.failed}</div>
            <div className="text-xs text-red-700 uppercase tracking-wide">Failed</div>
          </div>
        </div>

        {result.skipped.length > 0 && (
          <div className="text-xs">
            <div className="font-semibold text-amber-700 mb-1">Skipped:</div>
            <ul className="space-y-0.5">
              {result.skipped.map((s, i) => (
                <li key={i} className="text-zinc-600">
                  row {s.row}: <span className="font-mono">{s.email}</span> — {s.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
        {result.failed.length > 0 && (
          <div className="text-xs">
            <div className="font-semibold text-red-700 mb-1">Failed:</div>
            <ul className="space-y-0.5">
              {result.failed.map((s, i) => (
                <li key={i} className="text-zinc-600">
                  row {s.row}: <span className="font-mono">{s.email}</span> — {s.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setResult(null); setCsvText(''); }}
            className="rounded-md"
            data-testid="bulk-invite-another"
          >
            Invite more
          </Button>
          <Button
            size="sm"
            onClick={finish}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md"
            data-testid="bulk-invite-done"
          >
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-emerald-200 bg-emerald-50/30 rounded-md p-4 space-y-3" data-testid="bulk-invite-form">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-zinc-900">Bulk invite from CSV</div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const sample = 'email,name,role\njane@acme.com,Jane Doe,admin\njohn@acme.com,John Smith,manager\nmary@acme.com,Mary Lee,sales\npaul@acme.com,Paul Kim,sales';
            setCsvText(sample);
          }}
          className="text-xs h-6 text-zinc-500 hover:text-zinc-900"
          data-testid="bulk-invite-sample"
        >
          Use sample
        </Button>
      </div>

      <div className="text-xs text-zinc-600 bg-white border border-zinc-200 rounded-md p-2 font-mono">
        Expected format: <span className="text-emerald-700 font-semibold">email,name,role</span>
        <span className="text-zinc-400"> — role: admin | manager | sales (default: sales)</span>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={onFile}
          className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-emerald-100 file:text-emerald-700 hover:file:bg-emerald-200"
          data-testid="bulk-invite-file"
        />
        <span className="text-xs text-zinc-400">or paste below</span>
      </div>

      <Textarea
        value={csvText}
        onChange={(e) => setCsvText(e.target.value)}
        placeholder="jane@acme.com,Jane Doe,admin&#10;john@acme.com,John Smith,manager&#10;mary@acme.com,Mary Lee,sales"
        className="rounded-md resize-none h-32 font-mono text-xs bg-white"
        data-testid="bulk-invite-textarea"
      />

      {(parsed.entries.length > 0 || parsed.errors.length > 0) && (
        <div className="text-xs bg-white border border-zinc-200 rounded-md p-2 max-h-44 overflow-y-auto" data-testid="bulk-invite-preview">
          <div className="font-semibold text-zinc-900 mb-1">
            Preview: <span className="text-emerald-700">{parsed.entries.length} valid</span>
            {parsed.errors.length > 0 && <span className="text-red-700 ml-2">{parsed.errors.length} invalid</span>}
          </div>
          {parsed.entries.slice(0, 20).map((e) => (
            <div key={e.row} className="flex justify-between py-0.5 text-zinc-700">
              <span className="truncate">{e.name || '(no name)'} · <span className="font-mono">{e.email}</span></span>
              <span className="text-xs text-zinc-500">{e.role}</span>
            </div>
          ))}
          {parsed.entries.length > 20 && (
            <div className="text-zinc-400 text-xs italic mt-1">+ {parsed.entries.length - 20} more rows</div>
          )}
          {parsed.errors.length > 0 && (
            <div className="mt-2 pt-2 border-t border-zinc-100 text-red-700">
              {parsed.errors.slice(0, 5).map((e, i) => (
                <div key={i}>row {e.row}: {e.reason} — <span className="font-mono text-zinc-500">{e.raw}</span></div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setCsvText(''); onCancel(); }}
          className="rounded-md"
          data-testid="bulk-invite-cancel"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={submitting || parsed.entries.length === 0}
          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md"
          data-testid="bulk-invite-submit"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Layers className="w-3.5 h-3.5 mr-1.5" />}
          Invite {parsed.entries.length} {parsed.entries.length === 1 ? 'member' : 'members'}
        </Button>
      </div>
    </div>
  );
}


// =========================================================================
// OrgStatusBadge — pill showing lifecycle state (active/suspended/terminated)
// =========================================================================
function OrgStatusBadge({ status }) {
  const cfg = {
    active: { bg: 'bg-emerald-50', fg: 'text-emerald-700', border: 'border-emerald-200', label: 'Active' },
    suspended: { bg: 'bg-amber-50', fg: 'text-amber-800', border: 'border-amber-300', label: 'Suspended' },
    terminated: { bg: 'bg-red-50', fg: 'text-red-700', border: 'border-red-300', label: 'Terminated' },
  }[status] || { bg: 'bg-zinc-50', fg: 'text-zinc-500', border: 'border-zinc-200', label: status };
  return (
    <span
      className={`inline-flex items-center text-xs font-medium border rounded-full px-2 py-0.5 ${cfg.bg} ${cfg.fg} ${cfg.border}`}
      data-testid={`org-status-${status}`}
    >
      {cfg.label}
    </span>
  );
}

// =========================================================================
// LifecycleDialog — Suspend / Reactivate / Terminate / Delete an org
// =========================================================================
function LifecycleDialog({ org, onClose, onChanged }) {
  const [mode, setMode] = useState(null); // 'suspend' | 'reactivate' | 'terminate' | 'delete'
  const [reason, setReason] = useState('');
  const [retentionDays, setRetentionDays] = useState(30);
  const [confirmName, setConfirmName] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [forceDelete, setForceDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (org) {
      setMode(null);
      setReason(''); setRetentionDays(30);
      setConfirmName(''); setConfirmPhrase('');
      setForceDelete(false);
    }
  }, [org]);

  const status = org?.org_status || 'active';
  const deletionScheduled = org?.deletion_scheduled_for ? new Date(org.deletion_scheduled_for) : null;
  const retentionExpired = deletionScheduled && deletionScheduled <= new Date();

  const doSuspend = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/platform/orgs/${org.id}/suspend`, { reason: reason.trim() });
      toast.success(data.message);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suspend failed');
    } finally { setSubmitting(false); }
  };

  const doReactivate = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/platform/orgs/${org.id}/reactivate`, { reason: reason.trim() });
      toast.success(data.message);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reactivate failed');
    } finally { setSubmitting(false); }
  };

  const doTerminate = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/platform/orgs/${org.id}/terminate`, {
        reason: reason.trim(),
        retention_days: Number(retentionDays),
        confirm_org_name: confirmName.trim(),
      });
      toast.success(data.message);
      toast.info(`Data scheduled for deletion on ${new Date(data.deletion_scheduled_for).toLocaleDateString()}`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Terminate failed');
    } finally { setSubmitting(false); }
  };

  const doDelete = async () => {
    setSubmitting(true);
    try {
      const url = `${API}/platform/orgs/${org.id}?confirm_org_name=${encodeURIComponent(confirmName.trim())}&confirm_phrase=${encodeURIComponent(confirmPhrase.trim())}&force=${forceDelete ? 'true' : 'false'}`;
      const { data } = await axios.delete(url);
      toast.success(data.message);
      const counts = Object.entries(data.deleted_counts || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
      toast.info(`Purged: ${counts}`, { duration: 8000 });
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!org} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="lifecycle-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-600" />
            {org?.name} — Lifecycle
          </DialogTitle>
          <DialogDescription>
            Current status: <OrgStatusBadge status={status} />
            {deletionScheduled && (
              <span className="block text-xs text-zinc-600 mt-2">
                Data scheduled for deletion: <span className="font-semibold">{deletionScheduled.toLocaleString()}</span>
                {retentionExpired && <span className="text-red-700 ml-2">· retention expired</span>}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {!mode && (
          <div className="space-y-2 py-2">
            {status === 'active' && (
              <>
                <button
                  className="w-full text-left border border-amber-200 hover:bg-amber-50 rounded-md p-3 transition-colors"
                  onClick={() => setMode('suspend')}
                  data-testid="lc-action-suspend"
                >
                  <div className="flex items-center gap-2 font-semibold text-amber-800">
                    <span className="w-2 h-2 bg-amber-500 rounded-full" /> Suspend
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">
                    Members can still log in but ALL API calls are blocked (HTTP 423). Reversible.
                    Use for failed payment, dispute investigation, manual hold.
                  </div>
                </button>
                <button
                  className="w-full text-left border border-red-200 hover:bg-red-50 rounded-md p-3 transition-colors"
                  onClick={() => setMode('terminate')}
                  data-testid="lc-action-terminate"
                >
                  <div className="flex items-center gap-2 font-semibold text-red-700">
                    <span className="w-2 h-2 bg-red-500 rounded-full" /> Terminate
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">
                    Members blocked from login. Subscription cancelled. Data retained for N days then eligible for permanent deletion.
                    Use when a customer offboards.
                  </div>
                </button>
              </>
            )}
            {status === 'suspended' && (
              <>
                <button
                  className="w-full text-left border border-emerald-200 hover:bg-emerald-50 rounded-md p-3 transition-colors"
                  onClick={() => setMode('reactivate')}
                  data-testid="lc-action-reactivate"
                >
                  <div className="flex items-center gap-2 font-semibold text-emerald-700">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full" /> Reactivate
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">
                    Restore normal access. Members will need to log in again (their sessions were invalidated on suspend).
                  </div>
                </button>
                <button
                  className="w-full text-left border border-red-200 hover:bg-red-50 rounded-md p-3 transition-colors"
                  onClick={() => setMode('terminate')}
                  data-testid="lc-action-terminate"
                >
                  <div className="flex items-center gap-2 font-semibold text-red-700">
                    <span className="w-2 h-2 bg-red-500 rounded-full" /> Terminate
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">
                    Permanent off-boarding (with retention window).
                  </div>
                </button>
              </>
            )}
            {status === 'terminated' && (
              <>
                {!retentionExpired && (
                  <button
                    className="w-full text-left border border-emerald-200 hover:bg-emerald-50 rounded-md p-3 transition-colors"
                    onClick={() => setMode('reactivate')}
                    data-testid="lc-action-restore"
                  >
                    <div className="flex items-center gap-2 font-semibold text-emerald-700">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full" /> Restore
                    </div>
                    <div className="text-xs text-zinc-600 mt-1">
                      Cancel termination. Only available while data is still retained.
                    </div>
                  </button>
                )}
                <button
                  className="w-full text-left border border-red-300 hover:bg-red-50 rounded-md p-3 transition-colors"
                  onClick={() => setMode('delete')}
                  data-testid="lc-action-delete"
                >
                  <div className="flex items-center gap-2 font-semibold text-red-700">
                    <span className="w-2 h-2 bg-red-700 rounded-full" /> Permanently Delete
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">
                    ⚠ Irreversible. Wipes users, sessions, subscriptions, tokens, transcripts, summaries, and CRM credentials.
                    {!retentionExpired && <span className="block text-amber-700 mt-1">Retention window still active — requires force flag.</span>}
                  </div>
                </button>
              </>
            )}
          </div>
        )}

        {mode === 'suspend' && (
          <div className="space-y-3 py-2" data-testid="lc-form-suspend">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
              <div className="font-semibold text-amber-800">Suspend {org.name}</div>
              <div className="text-xs text-amber-700 mt-1">Members will see a friendly banner on every screen.</div>
            </div>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Reason (visible to the customer admin — e.g. 'Payment overdue, please update billing')"
              className="rounded-md h-24"
              data-testid="lc-reason"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setMode(null)} className="rounded-md">Back</Button>
              <Button
                size="sm"
                onClick={doSuspend}
                disabled={submitting || reason.trim().length < 3}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-md"
                data-testid="lc-suspend-confirm"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                Suspend account
              </Button>
            </div>
          </div>
        )}

        {mode === 'reactivate' && (
          <div className="space-y-3 py-2" data-testid="lc-form-reactivate">
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
              <div className="font-semibold text-emerald-800">Reactivate {org.name}</div>
              <div className="text-xs text-emerald-700 mt-1">Members regain full access. They&apos;ll need to log in again.</div>
            </div>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Reason (internal note — e.g. 'Payment received', 'Dispute resolved')"
              className="rounded-md h-20"
              data-testid="lc-reason"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setMode(null)} className="rounded-md">Back</Button>
              <Button
                size="sm"
                onClick={doReactivate}
                disabled={submitting || reason.trim().length < 3}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md"
                data-testid="lc-reactivate-confirm"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                Reactivate account
              </Button>
            </div>
          </div>
        )}

        {mode === 'terminate' && (
          <div className="space-y-3 py-2" data-testid="lc-form-terminate">
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm">
              <div className="font-semibold text-red-800">Terminate {org.name}</div>
              <div className="text-xs text-red-700 mt-1">
                Members will be locked out immediately. Active subscription will be cancelled.
                Data is retained for the period you set below before becoming eligible for permanent deletion.
              </div>
            </div>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Reason (e.g. 'Customer cancelled — confirmed by email 2026-02-14')"
              className="rounded-md h-20"
              data-testid="lc-reason"
            />
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">Retention period (days)</label>
              <Select value={String(retentionDays)} onValueChange={(v) => setRetentionDays(Number(v))}>
                <SelectTrigger className="rounded-md" data-testid="lc-retention"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Immediate (no retention — for trial cleanup)</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days (recommended — POPIA-compliant)</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">
                Type <span className="font-mono bg-red-100 text-red-800 px-1.5 rounded">{org.name}</span> to confirm
              </label>
              <Input
                value={confirmName}
                onChange={e => setConfirmName(e.target.value)}
                placeholder={org.name}
                className="rounded-md font-mono"
                data-testid="lc-confirm-name"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setMode(null)} className="rounded-md">Back</Button>
              <Button
                size="sm"
                onClick={doTerminate}
                disabled={submitting || reason.trim().length < 3 || confirmName.trim() !== org.name}
                className="bg-red-600 hover:bg-red-700 text-white rounded-md"
                data-testid="lc-terminate-confirm"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                Terminate account
              </Button>
            </div>
          </div>
        )}

        {mode === 'delete' && (
          <div className="space-y-3 py-2" data-testid="lc-form-delete">
            <div className="bg-red-50 border-2 border-red-400 rounded-md p-3 text-sm">
              <div className="font-bold text-red-900">⚠ PERMANENT DELETION</div>
              <div className="text-xs text-red-800 mt-1">
                This wipes ALL data for <span className="font-mono font-semibold">{org.name}</span>: users, sessions,
                transcripts, summaries, subscriptions, tokens, CRM credentials, and audit logs. It cannot be undone.
              </div>
            </div>
            {!retentionExpired && (
              <div className="bg-amber-50 border border-amber-300 rounded-md p-2 text-xs">
                <div className="font-semibold text-amber-800">Retention window still active.</div>
                <div className="text-amber-700">
                  Data is scheduled for deletion on {deletionScheduled?.toLocaleDateString()}.
                  Check &quot;force delete&quot; below to override this.
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">
                Type the org name <span className="font-mono bg-red-100 text-red-800 px-1.5 rounded">{org.name}</span>
              </label>
              <Input
                value={confirmName}
                onChange={e => setConfirmName(e.target.value)}
                placeholder={org.name}
                className="rounded-md font-mono"
                data-testid="lc-confirm-name"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700">
                Type <span className="font-mono bg-red-100 text-red-800 px-1.5 rounded">I understand this is irreversible</span>
              </label>
              <Input
                value={confirmPhrase}
                onChange={e => setConfirmPhrase(e.target.value)}
                placeholder="I understand this is irreversible"
                className="rounded-md font-mono text-xs"
                data-testid="lc-confirm-phrase"
              />
            </div>
            {!retentionExpired && (
              <label className="flex items-center gap-2 text-xs text-amber-800">
                <input
                  type="checkbox"
                  checked={forceDelete}
                  onChange={e => setForceDelete(e.target.checked)}
                  data-testid="lc-force-delete"
                />
                Force delete (bypass retention window)
              </label>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setMode(null)} className="rounded-md">Back</Button>
              <Button
                size="sm"
                onClick={doDelete}
                disabled={
                  submitting ||
                  confirmName.trim() !== org.name ||
                  confirmPhrase.trim() !== 'I understand this is irreversible' ||
                  (!retentionExpired && !forceDelete)
                }
                className="bg-red-700 hover:bg-red-800 text-white rounded-md"
                data-testid="lc-delete-confirm"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                Permanently Delete
              </Button>
            </div>
          </div>
        )}

        {!mode && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} className="rounded-md" data-testid="lc-close">Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}


// =========================================================================
// BillingHealthCard — at-a-glance view of dunning + lifecycle health.
// Surfaces "X orgs in dunning, R Y outstanding" so the platform owner can
// catch payment issues without drilling into every org.
// =========================================================================
function BillingHealthCard({ health, onViewOrgs }) {
  const [dunningOrgs, setDunningOrgs] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);

  const total = health?.total_in_dunning || 0;

  const loadDunning = useCallback(async () => {
    if (total === 0) { setDunningOrgs([]); return; }
    try {
      const { data } = await axios.get(`${API}/platform/dunning/status`);
      setDunningOrgs(data.in_dunning || []);
    } catch (_e) { /* ignore */ }
  }, [total]);

  useEffect(() => { loadDunning(); }, [loadDunning]);

  const sendReminder = async (orgId) => {
    setSendingReminder(orgId);
    try {
      const { data } = await axios.post(`${API}/platform/orgs/${orgId}/send-payment-reminder`);
      toast.success(data.message || 'Reminder sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  if (!health) {
    return null;
  }

  const outstanding = health.outstanding_zar || 0;
  const allHealthy = total === 0 && (health.suspended_orgs || 0) === 0 && (health.terminated_orgs || 0) === 0;

  const ladderItems = [
    { label: 'Grace', count: health.dunning_grace || 0, color: 'bg-zinc-100 text-zinc-700', desc: 'day 1–6' },
    { label: 'Warning', count: health.dunning_warning || 0, color: 'bg-amber-100 text-amber-800', desc: 'day 7–13' },
    { label: 'Suspended', count: health.dunning_suspended || 0, color: 'bg-red-100 text-red-700', desc: 'day 14+' },
  ];

  const stageColor = (s) => ({
    grace: 'text-zinc-700 bg-zinc-100',
    warning: 'text-amber-800 bg-amber-100',
    suspended: 'text-red-700 bg-red-100',
  }[s] || 'text-zinc-600 bg-zinc-50');

  return (
    <Card className="border-zinc-200" data-testid="billing-health-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Billing Health
          </CardTitle>
          {allHealthy ? (
            <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="billing-health-status">
              ✓ All clear
            </span>
          ) : (
            <span className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="billing-health-status">
              {total > 0 ? `${total} in dunning` : 'Action needed'}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Active orgs */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="bh-active">
            <div className="text-2xl font-bold text-emerald-700">{health.active_orgs || 0}</div>
            <div className="text-xs text-emerald-700/80 uppercase tracking-wide mt-1">Active</div>
          </div>
          {/* Suspended */}
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="bh-suspended">
            <div className="text-2xl font-bold text-amber-700">{health.suspended_orgs || 0}</div>
            <div className="text-xs text-amber-700/80 uppercase tracking-wide mt-1">Suspended</div>
          </div>
          {/* Terminated */}
          <div className="bg-red-50 border border-red-200 rounded-md p-3" data-testid="bh-terminated">
            <div className="text-2xl font-bold text-red-700">{health.terminated_orgs || 0}</div>
            <div className="text-xs text-red-700/80 uppercase tracking-wide mt-1">Terminated</div>
          </div>
          {/* Outstanding revenue */}
          <div className={`border rounded-md p-3 ${outstanding > 0 ? 'bg-red-50 border-red-200' : 'bg-zinc-50 border-zinc-200'}`} data-testid="bh-outstanding">
            <div className={`text-2xl font-bold ${outstanding > 0 ? 'text-red-700' : 'text-zinc-500'}`}>
              R{outstanding.toLocaleString()}
            </div>
            <div className={`text-xs uppercase tracking-wide mt-1 ${outstanding > 0 ? 'text-red-700/80' : 'text-zinc-500'}`}>
              Outstanding
            </div>
          </div>
        </div>

        {/* Dunning ladder */}
        {total > 0 && (
          <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="bh-ladder">
            <div className="text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-2">Dunning ladder</div>
            <div className="flex items-center gap-2 flex-wrap">
              {ladderItems.map((item) => (
                <div
                  key={item.label}
                  className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${item.color}`}
                  data-testid={`bh-ladder-${item.label.toLowerCase()}`}
                >
                  <span className="font-bold">{item.count}</span>
                  <span>{item.label}</span>
                  <span className="opacity-60 text-[10px]">{item.desc}</span>
                </div>
              ))}
              {onViewOrgs && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onViewOrgs}
                  className="text-xs h-7 ml-auto text-brand-primary hover:bg-brand-mute"
                  data-testid="bh-view-orgs"
                >
                  View organizations →
                </Button>
              )}
            </div>

            {/* Per-org dunning list with one-click reminder */}
            {dunningOrgs.length > 0 && (
              <div className="mt-3 border border-zinc-200 rounded-md divide-y divide-zinc-100" data-testid="bh-dunning-list">
                {dunningOrgs.map((d) => (
                  <div key={d.org_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`bh-dunning-row-${d.org_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`text-xs font-semibold uppercase rounded px-1.5 py-0.5 ${stageColor(d.dunning_stage)}`}>
                        {d.dunning_stage}
                      </span>
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-900 truncate">{d.org_name}</div>
                        <div className="text-xs text-zinc-500">
                          {d.plan} · {d.payment_failure_count || 0} failed attempt{(d.payment_failure_count || 0) === 1 ? '' : 's'}
                          {d.first_failure_at && (
                            <span> · since {new Date(d.first_failure_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => sendReminder(d.org_id)}
                      disabled={sendingReminder === d.org_id}
                      className="rounded-md text-xs h-7 px-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      data-testid={`bh-send-reminder-${d.org_id}`}
                    >
                      {sendingReminder === d.org_id ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <MessageSquare className="w-3 h-3 mr-1" />
                      )}
                      Send reminder
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

